import { useEffect, useState } from 'react';
import { useNavigate, generatePath } from 'react-router-dom';
import {
  IconArrowRight,
  IconSelected,
  IconClose,
  IconVisa,
  IconMasterCard,
  ClickOutsideDetector,
  IconFrozen,
  IconLoader,
  SidePanel,
  SidePanelType,
  InputText,
  formatBalance,
  ButtonPrimary,
  ButtonPseudo,
  Popup,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector } from '@store';
import Card from '../card/card';
import {
  CardStatus,
  CardPaymentSystem,
  CardType,
  CustomerType,
} from '../../../hoc/customers/types';
import { CardItemTypes } from './types';

import ROUTES from '../../../routes';

import cn from './cards.module.css';

const getStatusIcon = (status: CardStatus) => {
  let icon;

  switch (status) {
    case CardStatus.ORDERED:
      icon = <IconArrowRight />;
      break;
    case CardStatus.ACTIVE:
      icon = <IconSelected />;
      break;
    case CardStatus.CANCELED:
      icon = <IconClose />;
      break;
    case CardStatus.FROZEN:
      icon = <IconFrozen />;
      break;
    default:
      icon = <div />;
      break;
  }

  return icon;
};

const getCardStatusClassName = (status: CardStatus) => {
  let className;

  switch (status) {
    case CardStatus.NEW:
      className = cn.new;
      break;
    case CardStatus.ORDERED:
      className = cn.ordered;
      break;
    case CardStatus.ACTIVE:
      className = cn.active;
      break;
    case CardStatus.FROZEN:
      className = cn.frozen;
      break;
    case CardStatus.CANCELED:
      className = cn.canceled;
      break;
    case CardStatus.VOID:
      className = cn.void;
      break;
    default:
      break;
  }

  return className;
};

const CardItem = ({
  card,
  onStatusChange,
  updatedCard,
  statusChangeInProgress,
  customerId,
  zIndex,
  changeStatusInProgress,
  changeStatusSuccess,
  customerType,
}: CardItemTypes) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [limitsPanelVisible, setLimitsPanelVisible] = useState(false);
  const [limits, setLimits] = useState({
    perMonth: '',
    perWeek: '',
    indefinitely: '',
  });
  const [confirmPopupVisible, setConfirmPopupVisible] = useState(false);
  const [statusForChange, setStatusForChange] = useState<CardStatus>();

  const navigate = useNavigate();

  const { activePermissions } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const cardDate = new Date(card.expiredDate);

  const disabledClassName =
    card.status === CardStatus.FROZEN ||
    card.status === CardStatus.CANCELED ||
    card.status === CardStatus.VOID;

  let icon = <div />;
  let paymentSystem = 'UnionPay';

  if (card.paymentSystem === CardPaymentSystem.MASTER_CARD) {
    icon = (
      <IconMasterCard
        className={`${cn.customerCardIcon} ${cn.customerCardIconMastercard}`}
        width={32}
        height={19}
      />
    );
    paymentSystem = 'MC';
  } else if (card.paymentSystem === CardPaymentSystem.VISA) {
    icon = (
      <IconVisa
        className={`${cn.customerCardIcon} ${cn.customerCardIconVisa}`}
        width={40}
        height={13}
      />
    );
    paymentSystem = 'VISA';
  }

  useEffect(() => {
    if (changeStatusSuccess) {
      setConfirmPopupVisible(false);
    }
  }, [changeStatusSuccess]);

  return (
    <>
      <Card className={cn.customerCard} zIndex={zIndex}>
        <div
          className={`${cn.customerCardTop} ${
            disabledClassName ? cn.customerCardTopDisabled : ''
          }`}
          id={card.id}
        >
          <div className={cn.customerCardTopLeft}>
            {icon}
            <div className={cn.customerCardType}>
              {`${paymentSystem} ${
                card.type === CardType.DEBIT
                  ? CardType.DEBIT
                  : `debit ${CardType.CORP}`
              }`}
            </div>
            <div className={cn.virtual}>
              {card.virtual ? 'Virtual' : 'Physical'}
            </div>
            <div className={cn.customerCardNumber}>{card.number}</div>
            <div className={cn.customerCardDate}>
              {`${(cardDate.getMonth() + 1)
                .toString()
                .padStart(2, '0')}/${cardDate.getFullYear()}`}
            </div>
          </div>
          <div className={cn.customerCardTopRight}>
            <div className={cn.customerCardBalance}>
              {`${
                card?.balance ? formatBalance(Number(card.balance)) : '-'
              } ${card.balanceCurrency.toUpperCase()}`}
            </div>
            <div
              className={`${cn.customerCardStatus} ${getCardStatusClassName(
                card.status,
              )}`}
            >
              {statusChangeInProgress && card.id === updatedCard?.id ? (
                <IconLoader width={20} height={20} />
              ) : (
                card.status
              )}
            </div>
            <ClickOutsideDetector
              setOptionsListVisible={setMenuVisible}
              className={cn.customerCardMenuWrapper}
            >
              <>
                <button
                  type="button"
                  className={cn.customerCardMenuWrapperButton}
                  onClick={() => setMenuVisible(!menuVisible)}
                  aria-label="Menu button"
                >
                  <span />
                </button>
                {menuVisible && (
                  <ul className={cn.customerCardMenu}>
                    <li className={cn.customerCardMenuItem}>
                      <button
                        type="button"
                        className={cn.customerCardMenuButton}
                        onClick={() => {
                          setMenuVisible(false);
                          setLimitsPanelVisible(true);
                        }}
                      >
                        <span className={cn.statusName}>Limits</span>
                      </button>
                    </li>
                    <li className={cn.customerCardMenuItem}>
                      <button
                        type="button"
                        className={cn.customerCardMenuButton}
                        onClick={() => {
                          const link =
                            customerType === CustomerType.INDIVIDUAL
                              ? ROUTES.CUSTOMERS_INDIVIDUAL_CARDS_ORDER
                              : ROUTES.CUSTOMERS_CORPORATE_CARDS_ORDER;

                          setMenuVisible(false);
                          navigate(
                            generatePath(link, {
                              customerId: customerId as string,
                            }),
                          );
                        }}
                      >
                        <span className={cn.statusName}>Reissue</span>
                      </button>
                    </li>
                    {card.status === CardStatus.ORDERED && (
                      <li className={cn.customerCardMenuItem}>
                        <button
                          type="button"
                          className={cn.customerCardMenuButton}
                          onClick={() => {
                            const link =
                              customerType === CustomerType.INDIVIDUAL
                                ? ROUTES.CUSTOMERS_INDIVIDUAL_CARDS_EDIT
                                : ROUTES.CUSTOMERS_CORPORATE_CARDS_EDIT;

                            setMenuVisible(false);
                            navigate(
                              generatePath(link, {
                                customerId: customerId as string,
                                cardId: card.id,
                              }),
                            );
                          }}
                        >
                          <span className={cn.statusName}>Edit</span>
                        </button>
                      </li>
                    )}
                    {card.availableStatuses?.find(
                      (item) => item.value === CardStatus.ACTIVE,
                    ) &&
                      activePermissions?.includes(
                        PermissionEnum.CARDS_ACTIVATE_ACTION,
                      ) && (
                        <li className={cn.customerCardMenuItem}>
                          <button
                            type="button"
                            className={cn.customerCardMenuButton}
                            onClick={() => {
                              setConfirmPopupVisible(true);
                              setStatusForChange(CardStatus.ACTIVE);
                              setMenuVisible(false);
                            }}
                          >
                            <span className={cn.iconWrapper}>
                              {getStatusIcon(CardStatus.ACTIVE)}
                            </span>
                            <span className={cn.statusName}>Activate card</span>
                          </button>
                        </li>
                      )}

                    {card.availableStatuses?.find(
                      (item) => item.value === CardStatus.APPROVED,
                    ) &&
                      activePermissions?.includes(
                        PermissionEnum.CARDS_ACCEPT_ACTION,
                      ) && (
                        <li className={cn.customerCardMenuItem}>
                          <button
                            type="button"
                            className={cn.customerCardMenuButton}
                            onClick={() => {
                              setConfirmPopupVisible(true);
                              setStatusForChange(CardStatus.APPROVED);

                              setMenuVisible(false);
                            }}
                          >
                            <span className={cn.iconWrapper}>
                              {getStatusIcon(CardStatus.APPROVED)}
                            </span>
                            <span className={cn.statusName}>Approve card</span>
                          </button>
                        </li>
                      )}
                    {card.availableStatuses?.find(
                      (item) => item.value === CardStatus.ORDERED,
                    ) && (
                      <li className={cn.customerCardMenuItem}>
                        <button
                          type="button"
                          className={cn.customerCardMenuButton}
                          onClick={() => {
                            setConfirmPopupVisible(true);
                            setStatusForChange(CardStatus.ORDERED);

                            setMenuVisible(false);
                          }}
                        >
                          <span className={cn.iconWrapper}>
                            {getStatusIcon(CardStatus.ORDERED)}
                          </span>
                          <span className={cn.statusName}>Order card</span>
                        </button>
                      </li>
                    )}
                    {card.availableStatuses?.find(
                      (item) => item.value === CardStatus.FROZEN,
                    ) &&
                      activePermissions?.includes(
                        PermissionEnum.CARDS_FREEZE_ACTION,
                      ) && (
                        <li className={cn.customerCardMenuItem}>
                          <button
                            type="button"
                            className={cn.customerCardMenuButton}
                            onClick={() => {
                              setConfirmPopupVisible(true);
                              setStatusForChange(CardStatus.FROZEN);

                              setMenuVisible(false);
                            }}
                          >
                            <span className={cn.iconWrapper}>
                              {getStatusIcon(CardStatus.FROZEN)}
                            </span>
                            <span className={cn.statusName}>Freez card</span>
                          </button>
                        </li>
                      )}
                    {card.availableStatuses?.find(
                      (item) => item.value === CardStatus.CANCELED,
                    ) &&
                      activePermissions?.includes(
                        PermissionEnum.CARDS_CANCEL_ACTION,
                      ) && (
                        <li className={cn.customerCardMenuItem}>
                          <button
                            type="button"
                            className={cn.customerCardMenuButton}
                            onClick={() => {
                              setConfirmPopupVisible(true);
                              setStatusForChange(CardStatus.CANCELED);

                              setMenuVisible(false);
                            }}
                          >
                            <span className={cn.iconWrapper}>
                              {getStatusIcon(CardStatus.CANCELED)}
                            </span>
                            <span className={cn.statusName}>Cancel card</span>
                          </button>
                        </li>
                      )}
                  </ul>
                )}
              </>
            </ClickOutsideDetector>
          </div>
        </div>
        <div className={cn.customerCardBottom}>
          <div className={cn.customerCardBottomLeft}>
            <div className={cn.customerCardBottomItem}>
              {card.customerAccountId}
            </div>
            <div className={cn.customerCardBottomItem}>
              {`${card.customerFirstName} ${card.customerLastName}`}
            </div>
          </div>
          <div className={cn.customerCardBottomRight}>
            <div className={cn.customerCardAddress}>{card.address}</div>
          </div>
        </div>
      </Card>
      <Popup
        title={`Change status to ${statusForChange}`}
        onCancelClick={() => setConfirmPopupVisible(false)}
        onOkClick={() =>
          onStatusChange({ id: card.id, status: statusForChange as CardStatus })
        }
        cancelButtonText="Cancel"
        okButtonText={changeStatusInProgress ? 'Loading...' : 'Change'}
        isVisible={confirmPopupVisible}
      />
      <SidePanel
        type={SidePanelType.Fees}
        closeOnClickOutside
        isOpen={limitsPanelVisible}
        onClose={() => setLimitsPanelVisible(false)}
        title="Limits"
        sidepanelExpandedKey="customers-cards"
        footer={
          <>
            <ButtonPrimary
              text="Save"
              onClick={() => setLimitsPanelVisible(false)}
              bold
            />
            <ButtonPseudo
              text="Close"
              onClick={() => setLimitsPanelVisible(false)}
              bold
            />
          </>
        }
      >
        <div className={cn.limitsWrapper}>
          <div className={cn.orderCardFormGroup}>
            <InputText
              name="perMonth"
              placeholder="Per month limits"
              onChange={(e) =>
                setLimits({ ...limits, perMonth: e.target.value })
              }
              value={limits.perMonth}
              title="Per Month"
            />
          </div>
          <div className={cn.orderCardFormGroup}>
            <InputText
              name="perWeek"
              placeholder="Per week limits"
              onChange={(e) =>
                setLimits({ ...limits, perWeek: e.target.value })
              }
              value={limits.perWeek}
              title="Per Week"
            />
          </div>
          <div className={cn.orderCardFormGroup}>
            <InputText
              name="indefinitely"
              placeholder="Indefinitely limits"
              onChange={(e) =>
                setLimits({ ...limits, indefinitely: e.target.value })
              }
              value={limits.indefinitely}
              title="Indefinitely"
            />
          </div>
        </div>
      </SidePanel>
    </>
  );
};

export default CardItem;
